import { ControlValueAccessorValidatorBase } from '@fi-shared/control-value-accessor-validator-base';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
    ChangeDetectionStrategy,
    Component,
    Injector,
    Input,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { DesktopUserCredentialModel } from '@fi-app/core/desktop-user-credential.model';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiInfoIconMode } from '@fi-shared/info-icon/info-icon.component';
import { FiBrowserEventsService } from '@fi-shared/fi-shared-services/fi-browser-events.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'fi-login-credentials',
    templateUrl: './fi-login-credentials.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./fi-login-credentials.component.scss', '../../authenticate.scss', '../login.component.scss'],
    host: { class: 'fi-login-credentials' },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: FiLoginCredentialsComponent,
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: FiLoginCredentialsComponent,
        },
    ],
    standalone: false,
})
export class FiLoginCredentialsComponent
    extends ControlValueAccessorValidatorBase<DesktopUserCredentialModel>
    implements OnInit, OnDestroy
{
    @Input() OngoingUpgradeMessage: boolean;

    private isPasswordType: boolean = true;
    private isCapsOn: boolean = false;
    private isPasswordInputFocused: boolean = false;

    get userCredential(): DesktopUserCredentialModel {
        return this.ngModel;
    }
    get passwordType(): string {
        return this.isPasswordType ? 'password' : 'text';
    }
    get passwordSuffixIconClass(): { [key: string]: boolean } {
        return { 'fa-eye': this.isPasswordType, 'fa-eye-slash': !this.isPasswordType };
    }

    constructor(
        private injector: Injector,
        private fiStateService: FiStateService,
        private fiBrowserEventsService: FiBrowserEventsService,
    ) {
        super(injector);
    }

    ngOnInit() {
        this.subscribeOnDocumentKeyPress();
    }

    private subscribeOnDocumentKeyPress() {
        this.fiBrowserEventsService
            .getDocumentEventSubject$('keyup')
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(this.handleKeyPressFn);
        this.fiBrowserEventsService
            .getDocumentEventSubject$('keydown')
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(this.handleKeyPressFn);
    }
    private handleKeyPressFn = ($event: KeyboardEvent) => {
        this.isCapsOn = $event.getModifierState?.('CapsLock');
        this.setTooltipVisibility();
    };

    togglePasswordType() {
        this.isPasswordType = !this.isPasswordType;
        this.setTooltipVisibility();
    }

    setIsPasswordInputFocused(isFocused: boolean) {
        this.isPasswordInputFocused = isFocused;
        this.setTooltipVisibility();
    }

    isTooltipVisible$ = new BehaviorSubject<boolean>(false);
    private setTooltipVisibility() {
        this.isTooltipVisible$.next(this.isPasswordInputFocused && this.isCapsOn && this.isPasswordType);
    }

    goToLoginHelp() {
        this.fiStateService.go(FiStateNames.Login.LoginHelp);
    }

    private ngOnDestroy$ = new Subject<void>();
    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    protected readonly FiInfoIconMode = FiInfoIconMode;
}
